// Общий транспорт двух потоков. onMessage получает прежний формат пакета.
let authRecovery = null;

export function createManagedSocket(urlFactory, onMessage, onStatus, options = {}) {
  let socket = null;
  let stopped = false;
  let retryTimer = null;
  let heartbeatTimer = null;
  let openTimer = null;
  let attempts = 0;
  let lastPong = 0;
  const clearTimers = () => {
    clearTimeout(retryTimer); retryTimer = null;
    clearTimeout(openTimer); openTimer = null;
    clearInterval(heartbeatTimer); heartbeatTimer = null;
  };
  const safeCall = (fn, ...args) => {
    try {
      const result = fn?.(...args);
      if (result && typeof result.catch === 'function') result.catch((error) => console.error('Realtime callback failed', error));
    } catch (error) { console.error('Realtime callback failed', error); }
  };
  const send = (value) => {
    if (socket?.readyState !== WebSocket.OPEN) return false;
    try { socket.send(JSON.stringify(value)); return true; } catch { return false; }
  };
  const ping = () => send({ type: 'ping', timestamp: Date.now(),
    visible: document.visibilityState === 'visible' && document.hasFocus() });
  const schedule = () => {
    if (stopped || retryTimer || !navigator.onLine) return;
    const delay = Math.min(30000, 1000 * (2 ** Math.min(attempts++, 5))) + Math.floor(Math.random() * 500);
    retryTimer = setTimeout(() => { retryTimer = null; connect(); }, delay);
  };
  const connect = () => {
    if (stopped || !navigator.onLine || socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return;
    let current;
    try { current = new WebSocket(urlFactory()); } catch { schedule(); return; }
    socket = current;
    const active = () => !stopped && socket === current;
    openTimer = setTimeout(() => { if (active()) current.close(4000, 'connect timeout'); }, 15000);
    current.onopen = () => {
      if (!active()) { current.close(); return; }
      clearTimeout(openTimer); openTimer = null;
      attempts = 0; lastPong = Date.now();
      ping();
      heartbeatTimer = setInterval(() => {
        if (!active()) return;
        if (Date.now() - lastPong > 45000) current.close(4000, 'heartbeat timeout');
        else ping();
      }, 15000);
      safeCall(onStatus, true);
    };
    current.onmessage = (event) => {
      if (!active()) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pong') { lastPong = Date.now(); return; }
        safeCall(onMessage, data);
      } catch { console.warn('Invalid realtime packet'); }
    };
    current.onerror = () => { if (active()) safeCall(onStatus, false); };
    current.onclose = async (event) => {
      if (!active()) return;
      socket = null; clearTimers(); safeCall(onStatus, false);
      if (event.code === 4403 || event.code === 1007 || event.code === 1009) {
        stopped = true; removeListeners(); return;
      }
      if (event.code === 4401) {
        try {
          if (!options.onAuthExpired) throw new Error('Authentication expired');
          if (!authRecovery) authRecovery = Promise.resolve().then(options.onAuthExpired)
            .finally(() => { authRecovery = null; });
          await authRecovery;
        } catch {
          stopped = true; removeListeners(); return;
        }
      }
      schedule();
    };
  };
  const wake = () => {
    if (stopped) return;
    if (!socket || socket.readyState === WebSocket.CLOSED) {
      clearTimeout(retryTimer); retryTimer = null; connect();
    } else if (socket.readyState === WebSocket.OPEN) {
      if (Date.now() - lastPong > 45000) socket.close(4000, 'wake reconnect');
      else { ping(); safeCall(options.onWake); }
    }
  };
  const visibility = () => {
    send({ type: 'visibility', visible: document.visibilityState === 'visible' && document.hasFocus() });
    if (document.visibilityState === 'visible') wake();
  };
  const focus = () => { visibility(); };
  const offline = () => { socket?.close(4000, 'offline'); };
  function removeListeners() {
    window.removeEventListener('online', wake);
    window.removeEventListener('offline', offline);
    window.removeEventListener('focus', focus);
    window.removeEventListener('blur', visibility);
    document.removeEventListener('visibilitychange', visibility);
  }
  window.addEventListener('online', wake);
  window.addEventListener('offline', offline);
  window.addEventListener('focus', focus);
  window.addEventListener('blur', visibility);
  document.addEventListener('visibilitychange', visibility);
  connect();
  return {
    close() {
      stopped = true; clearTimers(); removeListeners();
      const current = socket; socket = null;
      current?.close(1000, 'component cleanup');
    },
    isConnected: () => socket?.readyState === WebSocket.OPEN,
    send,
  };
}